import { SidebarGroup, SidebarMenu } from "@/styles/ui/sidebar";

import { BlockItem, TreeViewTableItem } from "@/apis/preview/v2/types";

import {
  TreeViewSidebarContent,
  TreeViewSidebarLayout,
} from "./custom-sidebar";
import { AutoBuildButton } from "./tree/header";

import {
  TreeViewSidebarEmpty,
  TreeViewSidebarError,
  TreeViewSidebarLoading,
} from "./error";

import { usePreviewStore } from "@/stores/preview";
import { TreeItems } from "./new-tree-view/tree-items";

import { useMappingStore } from "@/stores/mapping";
import { SegmentItem } from "@/toggle-ui-kit/segment-control";
import { Dimension } from "@/types/mapping";
import { Loader2 } from "lucide-react";
import { TreeViewData } from "../table/hook";
import { PreviewEmptyView } from "../emtpy-view";
import { TextblockItem } from "./tree/textblock-item";
import {
  RemoveTagFromCellsArgs,
  useRemoveTagFromCells,
} from "./new-tree-view/use-remove-tag-from-cells";

export interface RenderTreeViewItemsProps {
  items: TreeViewTableItem[];
  blockItems: BlockItem[];
  dimension: "LINE_ITEM" | "AXIS" | "TEXTBLOCK";
  onRemoveTag?: (args: RemoveTagFromCellsArgs) => void;
}

const TreeViewItems = ({
  items,
  blockItems,
  dimension,
  onRemoveTag,
}: RenderTreeViewItemsProps) => {
  return (
    <>
      {dimension === "TEXTBLOCK" && blockItems.length > 0 && (
        <TextblockItem blockItems={blockItems} />
      )}
      {dimension !== "TEXTBLOCK" &&
        items.map((item, index) => {
          const treeViewItems =
            dimension === "LINE_ITEM" ? item.lineItem : item.axis;

          if (!treeViewItems || treeViewItems.length === 0) {
            return null;
          }

          return (
            <div
              key={`${dimension}-table-${item.roleKey}`}
              className="w-full p-[16px]"
            >
              <TreeItems
                dimension={dimension}
                treeViewItems={treeViewItems}
                item={item}
                roleKey={item.roleKey}
                onRemoveTag={onRemoveTag}
              />
              {index !== items.length - 1 && (
                <div className="w-full py-[16px]">
                  <div className="h-[8px] bg-neutral-200" />
                </div>
              )}
            </div>
          );
        })}
    </>
  );
};

export const PreviewSideBar = ({
  tableTreeView,
  isLoading,
  error,
}: {
  tableTreeView: TreeViewData;
  isLoading: boolean;
  error: Error | null;
}) => {
  const { dimension } = usePreviewStore((state) => state);

  if (isLoading) {
    return <TreeViewSidebarLoading />;
  }

  if (error) {
    return <TreeViewSidebarError />;
  }

  if (!tableTreeView || tableTreeView.items.length === 0) {
    return <TreeViewSidebarEmpty />;
  }

  return (
    <TreeViewSidebarLayout>
      <TreeViewSidebarContent>
        <SidebarGroup className="p-0 w-[500px] h-full overflow-y-auto scrollbar">
          <SidebarMenu>
            <TreeViewItems
              items={tableTreeView.items}
              blockItems={tableTreeView.blockItems}
              dimension={dimension}
            />
          </SidebarMenu>
        </SidebarGroup>
      </TreeViewSidebarContent>
    </TreeViewSidebarLayout>
  );
};

export const MappingTreeViewSidebarContent = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <div className="flex flex-col justify-between items-center self-stretch gap-[16px] h-full min-h-0 bg-white">
      <div className="flex flex-row justify-end items-start self-stretch gap-[16px] flex-shrink-0 h-[48px] px-[16px] bg-white pt-4">
        <div className="flex flex-row gap-2">
          <AutoBuildButton />
        </div>
      </div>
      <div className="flex-1 min-h-0 w-full">{children}</div>
    </div>
  );
};

export const checkExistsByDimension = (
  dimension: Dimension,
  tableTreeView?: TreeViewData | null,
) => {
  if (!tableTreeView) {
    return false;
  }

  const tables = tableTreeView.items;
  if (!tables || tables.length === 0) {
    return false;
  }

  switch (dimension) {
    case "LINE_ITEM":
      return tables[0].lineItem.some((item) => item.tagType === "LINE_ITEM");
    case "AXIS":
      return tables[0].axis.some((item) => item.tagType === "AXIS");
    default:
      return false;
  }
};

export const MappingTabTreeViewSideBar = ({
  tableTreeView,
  isLoading,
  error,
}: {
  tableTreeView?: TreeViewData | null;
  isLoading: boolean;
  error: Error | null;
}) => {
  const { dimension } = useMappingStore((state) => state.dimension);
  const { removeTagFromCells } = useRemoveTagFromCells();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="size-4 text-neutral-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return <TreeViewSidebarError />;
  }

  if (!tableTreeView || !checkExistsByDimension(dimension, tableTreeView)) {
    return <PreviewEmptyView />;
  }

  return (
    <TreeViewSidebarLayout className="w-full border-t-[1px] border-neutral-200">
      <MappingTreeViewSidebarContent>
        <SidebarGroup className="p-0 w-full h-full overflow-y-auto scrollbar">
          <SidebarMenu>
            <TreeViewItems
              items={tableTreeView.items}
              blockItems={tableTreeView.blockItems}
              dimension={dimension}
              onRemoveTag={removeTagFromCells}
            />
          </SidebarMenu>
        </SidebarGroup>
      </MappingTreeViewSidebarContent>
    </TreeViewSidebarLayout>
  );
};

export const MappingTabPreviewButton = ({
  selected = false,
  onClick,
}: {
  selected?: boolean;
  onClick?: () => void;
}) => {
  return (
    <SegmentItem selected={selected} size="small" onClick={onClick}>
      태그 빌딩
    </SegmentItem>
  );
};
